import { isBefore } from 'date-fns';

function filterNumberCharacter(character: string) {
  return /\d/.test(character);
}

/**
 * @param {string} time The inputValue from time picker will have a XX:XX xm approach, and we will want to get only the numbers, without colon, or meridiem
 * @returns {string[]} It should return a [X,X,X,X]
 */
function getArrayOfNumbersFromTimeText(time: string) {
  if (time.includes(':')) {
    const [hours, possibleMinutes] = time
      .split(':')
      .map((timeSlice) => timeSlice.split('').filter(filterNumberCharacter).join(''));
    const minutes =
      Number(possibleMinutes) < 5 ? possibleMinutes.padEnd(2, '0') : possibleMinutes.padStart(2, '0');

    return (hours.padStart(2, '0') + minutes).split('');
  }

  return time.split('').filter(filterNumberCharacter);
}

function getValidatedHour(stringHour: string, meridiem: 'am' | 'pm' | null) {
  const hour = Number(stringHour) > 24 ? Number(stringHour[0]) : Number(stringHour);
  if (hour % 12 === 0) {
    return hour - (meridiem === 'am' ? 12 : 0);
  }

  if (meridiem === 'pm' || (hour < 9 && !meridiem)) {
    return hour + 12;
  }
  return hour;
}

function getValidatedMinutes(stringMinutes = '') {
  const minutes = Number(stringMinutes.padEnd(2, '0').slice(0, 2));
  if (minutes >= 60) return Number(String(minutes)[0]);
  return minutes;
}

/**
 * We need to have preference from 9am to 5pm, since we are targeting businesses
 * Heather Miller: not many businesses have meetings in am, so we will have priority for pm
 * NOTE: We will try to read AM/PM first, and 24hour format next
 * @param timeText Since we have a RegExp for time picker, we can be sure that we will get XX:XX xm
 */
function getHoursFromString(timeText: string) {
  const hasMeridiem = timeText.toLowerCase().includes('a') || timeText.toLowerCase().includes('p');
  const isPm = timeText.toLowerCase().includes('p');
  const meridiem: 'am' | 'pm' | null = hasMeridiem ? `${isPm ? 'p' : 'a'}m` : null;
  const timeNumbers = getArrayOfNumbersFromTimeText(timeText);
  const possibleHour = timeNumbers.slice(0, 2).join('');

  if (timeText.includes(':')) {
    return {
      hours: getValidatedHour(possibleHour, meridiem),
      minutes: getValidatedMinutes(timeNumbers.slice(2, 4).join('')),
    };
  } else if (
    ['1', '2'].includes(timeNumbers[0]) &&
    (!timeNumbers[1] || (Number(timeNumbers[1]) > 0 && Number(timeNumbers[1]) < 6)) &&
    timeNumbers.length < 4
  ) {
    /**
     * 10 will fallback to 10:00am, assuming that users prefer to write 1 to set 1pm
     * 1:10pm, 1:20pm will have priority over 11:00, 12:00. Unless they provide 4 digits (1234 -> 12:34pm)
     * 1:30pm will have priority over 1:00pm
     */
    return {
      hours: getValidatedHour(timeNumbers[0], meridiem),
      minutes: getValidatedMinutes(timeNumbers.slice(1, 3).join('')),
    };
  }
  const hours = getValidatedHour(possibleHour, meridiem);
  const hoursInMeridiemFormat = hours % 12 || 12;
  const hourToSplit = String(
    new RegExp(`^${hours}`).test(timeNumbers.join('')) ? hours : hoursInMeridiemFormat,
  );
  const possibleMinutes = timeNumbers.join('').split(hourToSplit).slice(1).join(hourToSplit);

  return {
    hours,
    minutes: getValidatedMinutes(possibleMinutes),
  };
}

function getHoursFromSelectedOption(timeText: string) {
  const hasMeridiem = timeText.toLowerCase().includes('a') || timeText.toLowerCase().includes('p');
  if (hasMeridiem) {
    const isPm = timeText.toLowerCase().includes('p');
    let numbers = timeText.split('').filter(filterNumberCharacter).join('');
    const hours = Number(numbers.slice(0, -2));
    let offset = isPm ? 12 : 0;
    if (hours % 12 === 0) {
      offset = isPm ? 0 : -12;
    }

    return { hours: Number(numbers.slice(0, -2)) + offset, minutes: Number(numbers.slice(-2)) };
  }
  const [hours, minutes] = timeText.split(':').map(Number);
  return { hours, minutes };
}

function getDateFromTime(hours = 12, minutes = 0) {
  const value = new Date();
  value.setHours(hours, minutes, 0, 0);
  return value;
}

function getFormattedString(hours: number, minutes: number, is24Hours = false) {
  const formattedHours = is24Hours ? String(hours).padStart(2, '0') : hours % 12;
  const meridiem = hours >= 12 ? 'pm' : 'am';
  return `${formattedHours || 12}:${String(minutes).padStart(2, '0')}${is24Hours ? '' : ' ' + meridiem}`;
}

function createTimeOptions(interval = 15, is24Hours = false) {
  const availableMinutes = 60 * 24;
  const safeInterval = interval > 0 ? interval : 15;
  const amountOfOptions = Math.ceil(availableMinutes / safeInterval);

  return new Array(amountOfOptions).fill('').map((_, i) => {
    const minutes: number = (interval * i) % 60;
    const hours = Math.floor((interval * i) / 60);

    return getFormattedString(hours, minutes, is24Hours);
  });
}

function parseDateFromString(inputValue: string) {
  const { hours, minutes } = /([0-2]?[0-9]:[0-5][0-9] [ap]m|[0-2][0-9]:[0-5][0-9])/.test(inputValue)
    ? getHoursFromSelectedOption(inputValue)
    : getHoursFromString(inputValue);

  return getDateFromTime(hours, minutes);
}

function getHoursInMeridiem(value: Date) {
  return ((value.getHours() + 11) % 12) + 1;
}

function getMeridiem(value: Date) {
  return value.getHours() >= 12 ? 'pm' : 'am';
}

/**
 * @param {Date} value the current time picker value
 * @param {string[]} options available options of the time picker
 * @param {boolean} is24Hours if the format of the times is in 24 hours
 * @returns {string} It should return a string format valid time
 */
function getNearestTimeOption(value: Date, options: string[], is24Hours = false) {
  const meridiem = getMeridiem(value);
  const hoursInMeridiem = getHoursInMeridiem(value);
  const hours = value.getHours();
  const minutes = value.getMinutes();
  const meridiemCallback = (t: string) =>
    t.startsWith(`${hoursInMeridiem.toString()}:`) && t.endsWith(meridiem);
  const is24HoursCallback = (t: string) => t.startsWith(String(hours).padStart(2, '0'));
  const currentHour = `${is24Hours ? String(hours).padStart(2, '0') : hoursInMeridiem}:${String(
    minutes,
  ).padStart(2, '0')}${is24Hours ? '' : ' ' + meridiem}`;

  const closestHours = options.filter(is24Hours ? is24HoursCallback : meridiemCallback);
  let closestHour;

  for (let i = 0; i < closestHours.length; i++) {
    const parsedCurrentHour = parseDateFromString(currentHour);
    if (isBefore(parsedCurrentHour, parseDateFromString(closestHours[i]))) {
      closestHour = closestHours[i];
      break;
    }

    if (closestHours[closestHours.length - 1] === options[options.length - 1]) {
      closestHour = options[options.length - 1];
    } else {
      const lastOptionIndex = options.indexOf(closestHours[closestHours.length - 1]);
      closestHour = options[lastOptionIndex + 1];
    }
  }

  return closestHour;
}

export { parseDateFromString, createTimeOptions, getDateFromTime, getNearestTimeOption };
